import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Input, Tooltip, Select, Modal, Button, Popover, Popconfirm, Tag, Row, Col } from 'antd';
import { SearchOutlined, PushpinOutlined, PushpinFilled, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { toTime, toHrsMinsSecs } from '../../libs/formatLib';
import dayjs from '../../dayjs';
import RouteTitle from '../../components/RouteTitle';
import EditScheduleView from '../../editableList/schedules/EditScheduleViewV2';
import { FEATURE } from '../../model/features';
import { scheduleModelData } from '../../services/ModelService';
import { DEFAULT_EDITABLE_PROPS } from '../../editableList/EditableList';
import { Exception, OperatingDays, Period, Schedule } from '../../model/schedule'
import { ReactComponent as Edit } from '../../assets/icons/Edit.svg';
import { ReactComponent as Info } from '../../assets/icons/Info.svg';
import { ReactComponent as Trash } from "../../assets/icons/Trash.svg";
import { ReactComponent as Copy } from '../../assets/icons/Copy.svg';
import { ReactComponent as Divert } from '../../assets/icons/Divert.svg';
import { ReactComponent as Globe } from '../../assets/icons/Globe.svg';
import { FilterTags } from '../../containers/FilterTag';
import './GanttView.css';
import { forwardSchedule } from '../../services/routeService';
import { useAppContext } from '../../libs/contextLib';

const MIN_DAY_WIDTH = 26; // minimum pixels per day
const ROW_HEIGHT = 44; // pixels per row
const LABEL_WIDTH = 320; // width of duty code label column
const MAX_RENDERED_DAYS = 30;

// Add GanttCalendarRow component at the top level of GanttView
const GanttCalendarRow = React.memo(function GanttCalendarRow({
  calendarObj,
  isPinned,
  startDate,
  numberOfDays,
  expandedScheduleId,
  editModeScheduleId,
  setEditModeScheduleId,
  setExpandedScheduleId,
  editingCalendars,
  setEditingCalendars,
  originalCalendars,
  setOriginalCalendars,
  onScheduleUpdate,
  onCancelEdit,
  onDeleteSchedule,
  isAdmin,
  allTags,
  originalSchedules,
  filterItems,
  selectedDates,
  setSelectedDates,
  pinnedCalendars,
  setPinnedCalendars,
  handleReplicate,
  copySchedule,
  filterBy,
  sortBy,
  setSortBy,
  ganttTitle,
  manualScroll,
  setManualScroll,
  scrollToDate,
  headerContainerRef,
  contentRowsRef,
  hoveredDate,
  setHoveredDate,
  windowStart,
  WINDOW_SIZE,
  currentMonth,
  daysInMonth,
  dayWidth
}) {
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);
  const calendar = calendarObj;
  const runningDates = useMemo(
    () => new Schedule(calendarObj).getRunningDates(dayjs(startDate), numberOfDays),
    [calendarObj, startDate, numberOfDays]
  );
  let startTime = calendar.getStartTime?.();
  let endTime = calendar.getEndTime?.();
  if (!startTime || !endTime) return null;
  const style = {
    height: expandedScheduleId === calendar.scheduleId ? 'auto' : 44,
    transition: 'height 0.3s ease-in-out',
    background: isPinned ? '#fffbe6' : undefined
  };
  const displayStartTime = dayjs(startTime).format('DD/MM/YYYY');
  const displayEndTime = dayjs(endTime).format('DD/MM/YYYY');
  const { filteredPeriods, overlappingIds } = (() => {
    if (!calendar.schedulePeriods?.length) return { filteredPeriods: [], overlappingIds: new Set() };
    const sortedPeriods = [...calendar.schedulePeriods].sort((a, b) => dayjs(a.startDate).diff(dayjs(b.startDate)));
    const overlappingIds = new Set();
    for (let i = 0; i < sortedPeriods.length; i++) {
      const currentPeriod = sortedPeriods[i];
      const currentStart = dayjs(currentPeriod.startDate).startOf('day');
      const currentEnd = dayjs(currentPeriod.endDate).endOf('day');
      for (let j = i + 1; j < sortedPeriods.length; j++) {
        const nextPeriod = sortedPeriods[j];
        const nextStart = dayjs(nextPeriod.startDate).startOf('day');
        if (nextStart.isBefore(currentEnd)) {
          overlappingIds.add(currentPeriod.id);
          overlappingIds.add(nextPeriod.id);
        }
      }
    }
    return { filteredPeriods: sortedPeriods, overlappingIds };
  })();
  return (
    <div
      key={calendar.scheduleId}
      className={`gantt-row${isPinned ? ' pinned' : ''} ${expandedScheduleId === calendar.scheduleId ? 'expanded' : ''}`}
      style={style}
    >
      <div className={`duty-code-cell ${expandedScheduleId === calendar.scheduleId ? 'expanded-row' : ''}`}>
        <div className='d-flex justify-content-between align-items-center' style={{ width: '100%' }}
          onClick={() => {
            if (expandedScheduleId === calendar.scheduleId) {
              setExpandedScheduleId(null);
              setEditModeScheduleId(null);
            } else {
              setExpandedScheduleId(calendar.scheduleId);
              setEditModeScheduleId(null);
            }
          }}>
          <div className='d-flex align-items-center' style={{ wordBreak: 'break-word', maxWidth: 'calc(100% - 100px)', textWrap: 'auto', lineHeight: '14px' }}>
            {calendar.shared && <Globe style={{ width: '14px', height: '14px', marginRight: '6px' }} />}
            {calendar.scheduleName}
          </div>
          <div className='d-flex align-items-center' style={{ gap: '4px', flexShrink: 0 }}>
            {expandedScheduleId === calendar.scheduleId && (<>
              {!calendar.shared && <Tooltip mouseEnterDelay={0.6} title="Replicate Schedule to Regions" placement="bottom">
                <Button
                  className='icon-button btn-sm icon-only'
                  style={{ width: '26px', height: '26px' }}
                  icon={<Divert />}
                  onClick={async (e) => { e.stopPropagation(); await handleReplicate(calendar); }}
                />
              </Tooltip>}
              <Tooltip mouseEnterDelay={0.6} title="Copy Schedule" placement="bottom">
                <Button
                  className='icon-button btn-sm icon-only'
                  style={{ width: '26px', height: '26px' }}
                  icon={<Copy />}
                  onClick={async (e) => { e.stopPropagation(); await copySchedule(calendar); }}
                />
              </Tooltip>
              {(!calendar.shared || isAdmin) &&
                <Tooltip mouseEnterDelay={0.6} title="Edit Schedule" placement="bottom">
                  <Button className={`icon-button btn-sm icon-only edit-btn ${editModeScheduleId === calendar.scheduleId ? 'btn-active' : ''}`} style={{ width: '26px', height: '26px' }} icon={<Edit />} onClick={(e) => {
                    e.stopPropagation();
                    if (editModeScheduleId === calendar.scheduleId) {
                      const hasChanges = JSON.stringify(editingCalendars[calendar.scheduleId]) !== JSON.stringify(originalCalendars[calendar.scheduleId]);
                      if (hasChanges) {
                        if (window.confirm('You have unsaved changes. Do you want to discard them?')) {
                          setEditModeScheduleId(null);
                          setEditingCalendars(cals => {
                            const newCals = { ...cals };
                            delete newCals[calendar.scheduleId];
                            return newCals;
                          });
                        }
                      } else {
                        setEditModeScheduleId(null);
                      }
                    } else {
                      setEditModeScheduleId(calendar.scheduleId);
                      setOriginalCalendars(orig => ({ ...orig, [calendar.scheduleId]: calendar }));
                    }
                  }} />
                </Tooltip>
              }
            </>)}
            <Tooltip mouseEnterDelay={0.6} title={isPinned ? "Unpin Schedule" : "Pin Schedule"} placement="bottom">
              <Button
                className={`icon-button btn-sm icon-only pin-btn${isPinned ? ' btn-active' : ''}`}
                style={{ width: '26px', height: '26px' }}
                icon={<PushpinFilled />}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isPinned) {
                    setPinnedCalendars(pins => pins.filter(id => id !== calendar.scheduleId));
                  } else {
                    setPinnedCalendars(pins => pins.includes(calendar.scheduleId) ? pins : [...pins, calendar.scheduleId]);
                  }
                }}
              />
            </Tooltip>
            {expandedScheduleId === calendar.scheduleId && (
              <Popconfirm
                title="Delete this schedule?"
                popupVisible={deletePopupVisible}
                okText="Delete"
                okType="danger"
                cancelText="Cancel"
                onConfirm={() => {
                  onDeleteSchedule(calendar.scheduleId, calendar.shared);
                  setExpandedScheduleId(null);
                  setEditModeScheduleId(null);
                  setDeletePopupVisible(false);
                }}
                onCancel={() => {
                  setDeletePopupVisible(false);
                }}
              >
                <Tooltip mouseEnterDelay={0.6} title="Delete Schedule" placement="bottom">
                  <Button className='icon-button btn-sm icon-only btn-delete' style={{ width: '26px', height: '26px' }} icon={<Trash />} onClick={(e) => {
                    e.stopPropagation();
                    setDeletePopupVisible(true);
                  }} />
                </Tooltip>
              </Popconfirm>
            )}
          </div>
        </div>
        {expandedScheduleId === calendar.scheduleId && (
          <div className='CalendarView'>
            {editModeScheduleId === calendar.scheduleId ? (
              <div className='CalendarForm'>
                <EditScheduleView
                  editing={calendar}
                  handleItemUpdate={(updatedSchedule, { isFinal } = {}) => {
                    if (isFinal) {
                      if (onScheduleUpdate) {
                        onScheduleUpdate(updatedSchedule);
                      }
                      setEditModeScheduleId(null);
                      setOriginalCalendars(orig => ({ ...orig, [calendar.scheduleId]: updatedSchedule }));
                    } else {
                      setEditingCalendars(cals => ({ ...cals, [calendar.scheduleId]: updatedSchedule }));
                    }
                  }}
                  handleCancel={() => {
                    onCancelEdit(calendar.scheduleId);
                    setEditModeScheduleId(null);
                  }}
                  itemIdFn={(item) => item.scheduleId}
                  createItemFn={(item) => item}
                  isEditable={(item) => !item.shared}
                  validateFn={(item) => true}
                  {...DEFAULT_EDITABLE_PROPS}
                  allTags={allTags}
                />
                {originalSchedules[calendar.scheduleId] && (!calendar.shared || isAdmin) && (
                  <div className='d-flex align-items-center' style={{ marginTop: '-41px', height: '31px' }}>
                    <Popconfirm
                      title="Delete this schedule?"
                      okText="Delete"
                      okType="danger"
                      cancelText="Cancel"
                      onConfirm={() => onDeleteSchedule(calendar.scheduleId, calendar.shared)}
                    >
                      <Tooltip mouseEnterDelay={0.6} title="Delete Schedule" placement="bottom">
                        <Button className='icon-button btn-sm icon-only btn-delete' style={{ width: '26px', height: '26px' }} icon={<Trash />} />
                      </Tooltip>
                    </Popconfirm>
                  </div>
                )}
              </div>
            ) : (
              <div className='CalendarViewMode'>
                <div className='calendar-details'>
                  <Row gutter={[10, 15]}>
                    <Col xs={24} lg={24}>
                      <div className='detail-row'>
                        <label>Schedule Name:</label>
                        <div>{calendar.scheduleName || 'Unnamed Schedule'}</div>
                      </div>
                    </Col>
                    <Col xs={24} lg={24}>
                      <div className='detail-row'>
                        <label>Tags:</label>
                        <div className='simple-tags'>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {calendar.tags?.map(tag => (
                              <Tag key={tag} color="blue">{tag}</Tag>
                            ))}
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} lg={24}>
                      <div className='detail-row'>
                        <label>Included Schedule:</label>
                        <div>
                          {calendar.shared ?
                            calendar.schedulePeriods?.map(s => {
                              return <div key={s.scheduleId}>{s.start?.format('DD/MM/YYYY')} - {s.end?.format('DD/MM/YYYY')}</div>
                            }) : calendar.schedulePeriods?.length ?
                              `${calendar.schedulePeriods[0].start?.format('DD/MM/YYYY')} - ${calendar.schedulePeriods[0].end?.format('DD/MM/YYYY')}` :
                              calendar.includedSchedules?.length ?
                                calendar.includedSchedules.map(s => s.scheduleName).join(', ') :
                                'None'
                          }
                        </div>
                      </div>
                    </Col>
                    {!calendar.shared && <Col xs={24} lg={24}>
                      <div className='detail-row'>
                        <label>Operating Days:</label>
                        <div className="operating-days-tags simple-tags">
                          {calendar.operatingDays ?
                            (() => {
                              const days = Object.entries(calendar.operatingDays)
                                .filter(([day, enabled]) => enabled)
                                .map(([day]) => (
                                  <Tag key={day} color="blue">{day.charAt(0).toUpperCase() + day.slice(1, 3)}</Tag>
                                ));
                              return days.length > 0 ? days : 'None';
                            })()
                            : 'None'
                          }
                        </div>
                      </div>
                    </Col>}
                    {!calendar.shared &&
                      <Col xs={24} lg={24}>
                        <div className='detail-row'>
                          <label>Exceptions:</label>
                          {/* Show all the exception date ranges in a list (calendar.exception[].periods) */}
                          {calendar.exceptions?.length ? (
                            <ul className="schedule-exceptions-list" style={{ listStyleType: 'none', padding: 0, margin: 0, width: '100%' }}>
                              {calendar.exceptions.map((e, idx) => {

                                if (e.source) {
                                  return <li key={e.id || idx} className={`schedule-exception ${e.type === 'include' ? 'schedule-include' : 'schedule-exclude'}`}>
                                    {e.type?.charAt(0).toUpperCase() + e.type?.slice(1) || 'Exception'}: {e.source}
                                  </li>
                                }
                                const p = e.period;
                                if (!p) return null;
                                const start = p.start ? dayjs(p.start).format('YYYY-MM-DD') : '';
                                const end = p.end ? dayjs(p.end).format('YYYY-MM-DD') : '';
                                return (
                                  <li key={e.id || idx} className={`schedule-exception ${e.type === 'include' ? 'schedule-include' : 'schedule-exclude'}`}>
                                    {e.type?.charAt(0).toUpperCase() + e.type?.slice(1) || 'Exception'}: {start} - {end}
                                  </li>
                                );
                              })}
                            </ul>
                          ) : 'None'}
                        </div>
                      </Col>}
                    <Col xs={24} lg={24}>
                      <div className='detail-row'>
                        <label>Status:</label>
                        <div className='simple-tags'>
                          <FilterTags filterItems={filterItems} model={calendar} />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div
        className="gantt-row-timeline-wrapper"
        style={{ height: expandedScheduleId === calendar.scheduleId ? 'auto' : 44 }}
      >
        <div className="gantt-row-content"
        // style={{ width: WINDOW_SIZE * DAY_WIDTH, position: 'relative' }}
        >
          {[...Array(daysInMonth)].map((_, i) => {
            const currentDate = currentMonth.date(i + 1);
            const isWeekend = currentDate.day() === 0 || currentDate.day() === 6;
            const dateStr = currentDate.format('YYYY-MM-DD');
            const isRunning = runningDates.some(d => dayjs(d).isSame(currentDate, 'day'));
            return (
              <React.Fragment key={i}>
                {isWeekend && (
                  <div
                    className="weekend-column"
                    style={{
                      position: 'absolute',
                      left: `${i * dayWidth}px`,
                      width: `${dayWidth}px`,
                      top: 0,
                      bottom: 0,
                      background: '#f5f5f5',
                      pointerEvents: 'none',
                      zIndex: 0
                    }}
                  />
                )}
                {selectedDates.some(d => currentDate.isSame(d, 'day')) && (
                  <div
                    className="selected-date-column"
                    style={{
                      position: 'absolute',
                      left: `${i * dayWidth}px`,
                      width: `${dayWidth}px`,
                      top: 0,
                      bottom: 0,
                      background: 'rgba(250, 173, 20, 0.15)',
                      pointerEvents: 'none',
                      zIndex: 1
                    }}
                  />
                )}
                {isRunning && (
                  <Tooltip
                    title={<div>
                      <div style={{ fontWeight: 500 }}>{calendar.scheduleName || 'Calendar'}</div>
                      <div>Start: {displayStartTime}</div>
                      <div>End: {displayEndTime}</div>
                      <div>Duration: {filteredPeriods.length} periods</div>
                      <div style={{ marginTop: '4px' }}>
                        <FilterTags filterItems={filterItems} model={calendar} />
                      </div>
                      {calendar.tags?.length > 0 && (
                        <div style={{ marginTop: '4px' }}>
                          {calendar.tags.map(tag => (
                            <Tag key={tag} color="blue">{tag}</Tag>
                          ))}
                        </div>
                      )}
                    </div>}
                  >
                    <div
                      className="gantt-item sub-trip"
                      style={{
                        position: 'absolute',
                        left: `${i * dayWidth}px`,
                        width: `${dayWidth}px`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={() => setHoveredDate(dateStr)}
                      onMouseLeave={() => setHoveredDate(null)}
                    >
                    </div>
                  </Tooltip>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
});

const GanttView = ({
  calendars,
  publicSchedules,
  onSearch,
  sortBy,
  setSortBy,
  ganttTitle = 'Schedules',
  startDate = dayjs(), // Default to today
  numberOfDays = 365, // Default to show 7 days
  onScheduleUpdate, // Add this prop to handle schedule updates
  selectedDates = [],
  setSelectedDates = () => { },
  pinnedCalendars = [],
  setPinnedCalendars = () => { },
  manualScroll = false,
  setManualScroll = () => { },
  scrollToDate = null,
  expandedScheduleId = null,
  setExpandedScheduleId = () => { },
  onCancelEdit = () => { },
  onDeleteSchedule = () => { },
  filterItems, // Add filterItems prop
  allTags = [], // Accept allTags as a prop,
  editModeScheduleId = null,
  setEditModeScheduleId = () => { },
  originalSchedules = {}, // New prop for detecting unsaved schedules,
  filterBy = [],
  selectedMonth,
  setSelectedMonth,
  selectedYear,
  setSelectedYear,
  setFilterBy
}) => {
  const { isAdmin, setSchedules } = useAppContext();
  const headerContainerRef = useRef(null);
  const contentRowsRef = useRef(null);
  const scrollingElementRef = useRef(null); // NEW: track the active scroller
  const isSyncingRef = useRef(false); // NEW: prevent infinite loops
  const timelineContainerRef = useRef(null); // NEW: ref to measure timeline container width
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingCalendars, setEditingCalendars] = useState({}); // { [scheduleId]: editedCalendar }
  const [originalCalendars, setOriginalCalendars] = useState({}); // Store original versions
  const [hoveredDate, setHoveredDate] = useState(null); // hovered date string (YYYY-MM-DD)
  const [windowStart, setWindowStart] = useState(0); // NEW: start index of visible window
  const [dayWidth, setDayWidth] = useState(MIN_DAY_WIDTH); // NEW: dynamic day width
  const WINDOW_SIZE = 40; // Number of days to render at a time
  const currentMonth = dayjs(`${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`).startOf('month');
  const daysInMonth = currentMonth.daysInMonth();

  // Calculate dynamic day width based on container width
  useEffect(() => {
    const calculateDayWidth = () => {
      if (timelineContainerRef.current) {
        const containerWidth = timelineContainerRef.current.offsetWidth;
        const calculatedWidth = Math.max(MIN_DAY_WIDTH, containerWidth / daysInMonth);
        setDayWidth(calculatedWidth);
      }
    };

    calculateDayWidth();

    // Add resize listener
    const resizeObserver = new ResizeObserver(calculateDayWidth);
    if (timelineContainerRef.current) {
      resizeObserver.observe(timelineContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [daysInMonth]);

  // Calculate total timeline width based on number of days
  const TIMELINE_WIDTH = numberOfDays * dayWidth;

  // Memoize a map of scheduleId -> calendar for fast lookup
  const calendarMap = useMemo(() => {
    const map = new Map();
    calendars.forEach(c => map.set(c.scheduleId, c));
    return map;
  }, [calendars]);

  // Memoize orderedCalendars using the map for O(1) lookup
  const orderedCalendars = useMemo(() => {
    const pinned = pinnedCalendars
      .map(id => calendarMap.get(id))
      .filter(Boolean);
    const unpinned = calendars.filter(c => !pinnedCalendars.includes(c.scheduleId));
    // Only create new Schedule objects if the calendar data changes
    return [...pinned, ...unpinned].map(c => (c instanceof Schedule ? c : new Schedule(c)));
  }, [calendars, pinnedCalendars, calendarMap]);

  // Handle horizontal scroll synchronization
  useEffect(() => {
    const headerContainer = headerContainerRef.current;
    const contentRows = contentRowsRef.current;

    if (!headerContainer || !contentRows) return;

    const timelineWrappers = () =>
      contentRows.querySelectorAll('.gantt-row-timeline-wrapper');

    function handleScroll(e) {
      if (isSyncingRef.current) return;
      isSyncingRef.current = true;
      scrollingElementRef.current = e.target;

      const scrollLeft = e.target.scrollLeft;

      // Sync header
      if (e.target !== headerContainer) {
        headerContainer.scrollLeft = scrollLeft;
      }
      // Sync all rows
      timelineWrappers().forEach(wrapper => {
        if (wrapper !== e.target) {
          wrapper.scrollLeft = scrollLeft;
        }
      });

      // Release the lock after the frame
      requestAnimationFrame(() => {
        isSyncingRef.current = false;
      });
    }

    // Attach listeners
    headerContainer.addEventListener('scroll', handleScroll);
    timelineWrappers().forEach(wrapper => {
      wrapper.addEventListener('scroll', handleScroll);
    });

    // Cleanup
    return () => {
      headerContainer.removeEventListener('scroll', handleScroll);
      timelineWrappers().forEach(wrapper => {
        wrapper.removeEventListener('scroll', handleScroll);
      });
    };
  }, [calendars, pinnedCalendars]);

  // Handle scrolling to selected date
  useEffect(() => {
    if (scrollToDate && headerContainerRef.current && !manualScroll) {
      const left = timeToPosition(scrollToDate);
      headerContainerRef.current.scrollLeft = left;
    }
  }, [scrollToDate]);

  // Function to check if a date is a public holiday and get its name
  const getHolidayInfo = (date) => {
    return null;
  };

  // Generate timeline headers with months and days
  const renderTimelineHeaders = () => {
    return (
      <div className="Calendar timeline-header">
        <div className="month-row" style={{ position: 'relative', width: daysInMonth * dayWidth }}>
          <div
            className="Calendar timeline-header-cell month-cell"
            style={{
              width: daysInMonth * dayWidth,
              position: 'absolute',
              left: 0,
            }}
          >
            {currentMonth.format('MMMM YYYY')}
          </div>
        </div>
        <div className="day-row" style={{ position: 'relative', display: 'flex', alignItems: 'center', width: daysInMonth * dayWidth }}>
          {[...Array(daysInMonth)].map((_, i) => {
            const currentDate = currentMonth.date(i + 1);
            const isSelected = selectedDates.some(d => currentDate.isSame(d, 'day'));
            const isHovered = hoveredDate === currentDate.format('YYYY-MM-DD');
            const isWeekend = currentDate.day() === 0 || currentDate.day() === 6;
            return (
              <div
                key={`day-${i}`}
                className={`Calendar timeline-header-cell day-cell${isSelected ? ' selected-date' : ''}${isHovered ? ' hovered-col' : ''}${isWeekend ? ' weekend' : ''}`}
                style={{
                  width: dayWidth,
                  flexShrink: 0,
                  cursor: 'pointer',
                  background: isSelected ? '#ffe58f' : isHovered ? '#e6f7ff' : isWeekend ? '#f5f5f5' : undefined,
                  borderBottom: isSelected ? '2px solid #faad14' : isHovered ? '2px solid #1890ff' : undefined
                }}
                onClick={() => {
                  setSelectedDates(dates => {
                    if (dates.some(d => currentDate.isSame(d, 'day'))) {
                      return dates.filter(d => !currentDate.isSame(d, 'day'));
                    } else {
                      return [...dates, currentDate];
                    }
                  });
                }}
              >
                <div className="date-header">
                  <div style={{ color: isWeekend ? '#999' : undefined }}>{currentDate.format('DD')}</div>
                  <div style={{ color: isWeekend ? '#999' : undefined }}>{currentDate.format('ddd')[0]}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Convert time to position
  const timeToPosition = (timeStr) => {
    if (!timeStr) return 0;

    const date = dayjs(timeStr).startOf('day');
    const startOfTimeline = dayjs(startDate).startOf('day');
    const diffInDays = date.diff(startOfTimeline, 'days', true);
    return diffInDays * dayWidth;
  };

  // Calculate position and width for items
  const calculateItemStyle = (startTime, endTime) => {
    const left = timeToPosition(startTime);
    const width = timeToPosition(endTime) - left + dayWidth; // Add one day width to include the end date

    return {
      left: `${left}px`,
      width: `${Math.max(width, 30)}px`
    };
  };

  // Format time for display
  const formatTime = (date) => {
    if (!date) return '';
    return dayjs(date).format('DD/MM/YYYY');
  };

  // Detect overlapping periods
  const detectOverlaps = (periods) => {
    if (!periods?.length) return { filteredPeriods: [], overlappingIds: new Set() };

    const sortedPeriods = [...periods].sort((a, b) => {
      const aTime = dayjs(a.startDate).startOf('day');
      const bTime = dayjs(b.startDate).startOf('day');
      return aTime.diff(bTime);
    });

    const overlappingIds = new Set();

    for (let i = 0; i < sortedPeriods.length; i++) {
      const currentPeriod = sortedPeriods[i];
      const currentStart = dayjs(currentPeriod.startDate).startOf('day');
      const currentEnd = dayjs(currentPeriod.endDate).endOf('day');

      for (let j = i + 1; j < sortedPeriods.length; j++) {
        const nextPeriod = sortedPeriods[j];
        const nextStart = dayjs(nextPeriod.startDate).startOf('day');

        if (nextStart.isBefore(currentEnd)) {
          overlappingIds.add(currentPeriod.id);
          overlappingIds.add(nextPeriod.id);
        }
      }
    }

    return {
      filteredPeriods: sortedPeriods,
      overlappingIds
    };
  };

  // Handle sort
  const handleSortChange = (value) => {
    setSortBy(value);
  };

  const handleReplicate = async (calendar) => {
    if (!window.confirm(`Are you sure you want to copy ${calendar.scheduleName} to all of your regions?`)) {
      return;
    }

    await forwardSchedule(calendar)
  }

  const copySchedule = async (calendar) => {
    if (!window.confirm(`Are you sure you want to copy ${calendar.scheduleName}?`)) {
      return;
    }
    const copiedCalendar = Schedule.clone({ ...calendar, shared: undefined, scheduleName: `${calendar.scheduleName} (Copy)` })
    if(calendar.shared) { 
      // we should use the first schedule as the only schedulePeriod and the rest in includedSchedules
      copiedCalendar.schedulePeriods = []
      copiedCalendar.exceptions = calendar.schedulePeriods.map(p => new Exception({
        type: 'include',
        period: p,
      }))
      // copiedCalendar.operatingDays = new OperatingDays([true, true, true, true, true, true, true])
    }
    const copied = await scheduleModelData.copy(copiedCalendar)
    setSchedules(schedules => ({ [copied.scheduleId]: copied, ...schedules }));
    // setEditingCalendars(cals => ({ ...cals, [copied.scheduleId]: copied }));
    // setOriginalCalendars(orig => ({ ...orig, [copied.scheduleId]: copied }));
    setEditModeScheduleId(copied.scheduleId);
    setExpandedScheduleId(copied.scheduleId);
    // if in public calendar view switch to private calendar view
    if(filterBy.includes('Shared')) {
      setFilterBy(filterBy => filterBy.map(f => f === 'Shared' ? 'Private' : f));
    }
  }

  return (
    <div className="gantt-view">
      <div className="gantt-container">
        {/* Header row */}
        <div className="header-row">
          <div className="duty-code-header" style={{ color: '#777' }}>{ganttTitle}</div>
          <div className="timeline-header-container" ref={headerContainerRef}>
            <div className="timeline-header" ref={timelineContainerRef}>
              {renderTimelineHeaders()}
            </div>
          </div>
        </div>

        {/* Content rows */}
        <div className="content-rows" ref={contentRowsRef}>
          {/* Render all rows with a single .map */}
          {orderedCalendars.map(calendarObj => (
            <GanttCalendarRow
              key={calendarObj.scheduleId}
              calendarObj={calendarObj}
              isPinned={pinnedCalendars.includes(calendarObj.scheduleId)}
              startDate={startDate}
              numberOfDays={numberOfDays}
              expandedScheduleId={expandedScheduleId}
              editModeScheduleId={editModeScheduleId}
              setEditModeScheduleId={setEditModeScheduleId}
              setExpandedScheduleId={setExpandedScheduleId}
              editingCalendars={editingCalendars}
              setEditingCalendars={setEditingCalendars}
              originalCalendars={originalCalendars}
              setOriginalCalendars={setOriginalCalendars}
              onScheduleUpdate={onScheduleUpdate}
              onCancelEdit={onCancelEdit}
              onDeleteSchedule={onDeleteSchedule}
              isAdmin={isAdmin}
              allTags={allTags}
              originalSchedules={originalSchedules}
              filterItems={filterItems}
              selectedDates={selectedDates}
              setSelectedDates={setSelectedDates}
              pinnedCalendars={pinnedCalendars}
              setPinnedCalendars={setPinnedCalendars}
              handleReplicate={handleReplicate}
              copySchedule={copySchedule}
              filterBy={filterBy}
              sortBy={sortBy}
              setSortBy={setSortBy}
              ganttTitle={ganttTitle}
              manualScroll={manualScroll}
              setManualScroll={setManualScroll}
              scrollToDate={scrollToDate}
              headerContainerRef={headerContainerRef}
              contentRowsRef={contentRowsRef}
              hoveredDate={hoveredDate}
              setHoveredDate={setHoveredDate}
              windowStart={windowStart}
              WINDOW_SIZE={WINDOW_SIZE}
              currentMonth={currentMonth}
              daysInMonth={daysInMonth}
              dayWidth={dayWidth}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default GanttView; 